import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVolver } from '@/shared/hooks/useVolver';
import { mealsApi, type CreateMealInput } from '@/shared/api/endpoints';
import { NetworkError } from '@/shared/api/httpClient';
import type { MealDefaults, MealType, MeasurementUnit } from '@/shared/api/types';
import { formatTime } from '@/shared/format/dates';
import { MEAL_TYPES, mealTypeLabel, unitLabel } from '@/shared/format/nutrition';
import { describeError, useAsync } from '@/shared/hooks/useAsync';
import { mealQueue } from '@/shared/offline/queue';
import { Banner, Button, Card, Chip, Eyebrow, Keypad, Spinner } from '@/shared/ui';
import { BackIcon, CloseIcon } from '@/shared/ui/icons';
import { captureStore } from './captureStore';

const UNITS: MeasurementUnit[] = ['Gram', 'Milliliter', 'Unit', 'Serving'];

/**
 * S-03 · la declaración.
 *
 * Cuatro decisiones y nada más: qué fue, cuánto, en qué unidad y de qué comida. Todo lo demás
 * —fotos, rótulos, correcciones— pasó antes o pasa después, en el detalle. La resolución no
 * bloquea nada (RF-17): esto se guarda y el motor trabaja mientras la persona sigue con su vida.
 */
export function LogMealPage() {
  const navigate = useNavigate();
  const volver = useVolver('/');
  const capture = captureStore.peek();
  const defaults = useAsync<MealDefaults>(() => mealsApi.defaults(), []);

  const [freeText, setFreeText] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState<MeasurementUnit | ''>('Gram');
  const [type, setType] = useState<MealType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* El almacén de captura vive fuera de React, así que quitar una foto no vuelve a renderizar
     por sí solo. Este contador es lo único que le avisa a la pantalla que la tanda cambió. */
  const [, setPhotoRevision] = useState(0);

  if (defaults.loading && defaults.data === null) {
    return <Spinner label="Preparando el registro" />;
  }

  // Sin defaults del servidor —típicamente por falta de red— registrar sigue siendo posible: el
  // momento es ahora y el tipo lo elige la persona.
  const suggested = defaults.data?.suggestedType ?? 'Snack';
  const effectiveType = type ?? suggested;
  const occurredAt = defaults.data?.occurredAt ?? new Date().toISOString();

  /*
   * Una foto es una declaración.
   *
   * Antes esto miraba sólo el texto y el código de barras, así que registrar una comida con la
   * foto sola era imposible: el botón quedaba muerto y sin explicación, en la misma pantalla que
   * dice «Foto del plato lista. Se estima en segundo plano». El resolutor de visión (RN-21) existe
   * exactamente para ese caso — es el que convierte una foto sin texto en una estimación— y la
   * pantalla lo estaba bloqueando.
   *
   * RF-09 hace opcional cada campo por separado y RF-10 admite cero o más imágenes. Lo único que
   * de verdad no se puede registrar es un ítem sin nada: sin texto, sin código y sin foto no hay
   * qué resolver.
   */
  const declared =
    freeText.trim() !== '' || (capture?.ean ?? '') !== '' || (capture?.files.length ?? 0) > 0;

  const submit = async () => {
    setSubmitting(true);
    setError(null);

    const payload: CreateMealInput = {
      occurredAt,
      type: effectiveType,
      note: null,
      items: [
        {
          freeText: freeText.trim() === '' ? null : freeText.trim(),
          ean: capture?.ean ?? null,
          quantity: quantity === '' ? null : Number(quantity.replace(',', '.')),
          unit: unit === '' ? null : unit,
        },
      ],
    };

    try {
      const meal = await mealsApi.create(payload);
      const item = meal.items[0];

      /*
       * Las imágenes van de a una y en orden, no en paralelo: cada subida dispara una nueva
       * resolución del ítem, y mandarlas juntas haría competir varias pasadas de la cascada por el
       * mismo ítem. Con `for` la última en subir es la que deja el estado final.
       */
      if (item !== undefined) {
        for (const file of capture?.files ?? []) {
          await mealsApi.attachImage(meal.id, item.id, file, capture!.role);
        }
      }

      captureStore.clear();
      navigate(item === undefined ? '/' : `/comidas/${meal.id}/items/${item.id}`, {
        replace: true,
      });
    } catch (cause) {
      /*
       * RF-49 · sin red la comida no se pierde ni se le pide a la persona que la recuerde. La foto
       * no viaja en la cola —serializar megabytes en `localStorage` la llenaría— así que se
       * encola la declaración y la foto se pierde. Se dice, no se oculta.
       */
      if (cause instanceof NetworkError) {
        mealQueue.enqueue(payload);
        captureStore.clear();
        setQueued(true);
        setSubmitting(false);
        return;
      }

      setError(describeError(cause));
      setSubmitting(false);
    }
  };

  if (queued) {
    return (
      <div className="stack">
        <h1>Guardada sin conexión</h1>
        <Banner tone="info">
          Tu comida quedó guardada en este dispositivo y se envía sola cuando vuelva la señal.
          {(capture?.files.length ?? 0) > 0 &&
            ` ${capture!.files.length === 1 ? 'La foto no se pudo guardar: sacala' : 'Las fotos no se pudieron guardar: sacalas'} de nuevo cuando sincronice.`}
        </Banner>
        <Button variant="primary" block onClick={() => navigate('/')}>
          Volver
        </Button>
      </div>
    );
  }

  return (
    <>
      <header className="row-between">
        <button
          type="button"
          className="btn-square"
          aria-label="Volver"
          onClick={volver}
        >
          <BackIcon size={18} />
        </button>
        <h1 style={{ fontSize: 'var(--text-h2)' }}>Qué comiste</h1>
        {/*
          Saltear también respeta `declared`. Llamaba a `submit()` sin el guard, así que desde una
          declaración sin foto habría creado un ítem sin nada: ni texto, ni código, ni imagen —
          nada que la cascada pueda resolver.
        */}
        <Button variant="plain" onClick={() => void submit()} disabled={submitting || !declared}>
          Saltear
        </Button>
      </header>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      {capture !== null && capture.previewUrls.length > 0 && (
        <Card size="tight">
          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            <div className="stack" style={{ gap: 2 }}>
              <strong className="micro">{photosReady(capture.files.length, capture.role)}</strong>
              <span className="nano muted">
                Se estima en segundo plano. Lo que escribas acá la mejora.
              </span>
            </div>

            {/* Cada una se puede sacar de la tanda: elegiste seis del carrete y dos eran del gato. */}
            <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {capture.previewUrls.map((url, index) => (
                <div key={url} style={{ position: 'relative' }}>
                  <img
                    src={url}
                    alt={`Foto ${index + 1} de ${capture.previewUrls.length}`}
                    style={{
                      width: 62,
                      height: 62,
                      borderRadius: 'var(--radius-control)',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <button
                    type="button"
                    className="thumb-remove"
                    aria-label={`Quitar la foto ${index + 1}`}
                    onClick={() => {
                      captureStore.removeAt(index);
                      setPhotoRevision((current) => current + 1);
                    }}
                  >
                    <CloseIcon size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {capture?.ean != null && (
        <Card size="tight">
          <Eyebrow>Código leído</Eyebrow>
          <strong className="numeric">{capture.ean}</strong>
        </Card>
      )}

      <div className="field">
        <label htmlFor="declaration">Descripción</label>
        <input
          id="declaration"
          autoFocus
          placeholder="Milanesa de pollo con puré"
          value={freeText}
          onChange={(event) => setFreeText(event.target.value)}
        />
      </div>

      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        <Eyebrow>Cantidad</Eyebrow>
        <div className="figure" aria-live="polite">
          <span className="figure-value" style={{ fontSize: 'var(--text-figure)' }}>
            {quantity === '' ? '0' : quantity}
          </span>
          <span className="figure-unit">{unit === '' ? 'sin declarar' : unitLabel(unit)}</span>
        </div>
        <div className="segmented" role="group" aria-label="Unidad">
          {UNITS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={unit === option}
              onClick={() => setUnit(unit === option ? '' : option)}
            >
              {unitLabel(option)}
            </button>
          ))}
        </div>
      </div>

      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        <Eyebrow>Qué comida</Eyebrow>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {MEAL_TYPES.map((candidate) => (
            <Chip
              key={candidate}
              large
              aria-pressed={candidate === effectiveType}
              onClick={() => setType(candidate)}
            >
              {mealTypeLabel(candidate)}
              {/* RN-19 · el tipo que propone la hora local se muestra con esa hora, no a secas. */}
              {candidate === suggested && ` · ${formatTime(occurredAt)}`}
            </Chip>
          ))}
        </div>
      </div>

      <div className="sticky-foot">
        <Button
          variant="primary"
          block
          onClick={() => void submit()}
          disabled={submitting || !declared}
        >
          {submitting ? 'Registrando…' : 'Registrar y seguir'}
        </Button>

        {/*
          Un botón deshabilitado sin motivo es un callejón: se toca, no pasa nada, y no hay dónde
          leer por qué. Sólo aparece cuando de verdad no hay nada declarado.
        */}
        {!declared && (
          <p className="micro muted" role="status">
            Escribí qué comiste, sacale una foto o leé su código de barras. Con cualquiera de las
            tres alcanza.
          </p>
        )}

        {/*
          Teclado propio: el del sistema tapa media pantalla y cambia de alto entre dispositivos,
          lo que mueve el botón primario justo cuando el pulgar va a tocarlo.
        */}
        <Keypad
          onDigit={(digit) => setQuantity((current) => `${current}${digit}`)}
          onDecimal={() =>
            setQuantity((current) => (current.includes(',') ? current : `${current || '0'},`))
          }
          onBackspace={() => setQuantity((current) => current.slice(0, -1))}
        />
      </div>
    </>
  );
}

/** «Foto del plato lista» no sirve cuando son tres. */
function photosReady(count: number, role: 'Dish' | 'NutritionLabel'): string {
  const subject = role === 'Dish' ? 'del plato' : 'del rótulo';

  return count === 1 ? `Foto ${subject} lista` : `${count} fotos ${subject} listas`;
}
